import React, { useState, useEffect } from "react";
import { MapPin, Phone, Mail, Facebook, Linkedin, Twitter, Instagram, ArrowUp, Activity, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="42127" className="flex flex-col gap-3">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-24 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <ul className="space-y-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-slate-400 hover:text-cyan-400 transition-colors duration-200 text-sm font-medium flex items-center gap-2 group">
            <span className="h-1 w-1 rounded-full bg-cyan-400/0 group-hover:bg-cyan-400 transition-all duration-300" />
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  const currentYear = new Date().getFullYear();
  return <section id="footer" className="dark bg-[#020617] text-foreground relative overflow-hidden border-t border-white/10">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-50" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 py-16 relative z-10" ref={ref}>
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {}
          <div className="space-y-6">
            <Link to="/HomePage" className="block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo.png" alt="Ds-Services logo" className="h-7" />
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed">
              Η <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo.png" alt="Ds-Services logo" className="h-5 inline-block align-baseline" /> προσφέρει ολοκληρωμένες λύσεις πληροφορικής για μικρές επιχειρήσεις. 
              Από δίκτυα και servers μέχρι συστήματα ασφαλείας, access controls και web development, 
              είμαστε ο τεχνικός σας συνεργάτης.
            </p>
            <div className="flex items-center gap-2 text-cyan-400 text-xs font-mono bg-cyan-500/10 p-2 rounded border border-cyan-500/20 w-fit">
              <ShieldCheck className="h-4 w-4" />
              <span>SECURE INFRASTRUCTURE</span>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-bold tracking-tight text-foreground flex items-center gap-2 uppercase font-sans">Υπηρεσιες &amp; Πλοηγηση</h3>
            <MenuProvider menu_id="37" className="w-full">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-bold tracking-tight text-foreground uppercase font-sans">Επικοινωνια</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-slate-400 group">
                <MapPin className="h-5 w-5 text-cyan-400 shrink-0 mt-0.5 group-hover:text-cyan-300 transition-colors" />
                <span>Αθήνα, Ελλάδα</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400 group">
                <Mail className="h-5 w-5 text-cyan-400 shrink-0 group-hover:text-cyan-300 transition-colors" />
                <Link to="mailto:info@ds-services.gr" className="hover:text-foreground transition-colors">
                  info@ds-services.gr
                </Link>
              </li>
              <li className="pt-2">
                <div className="text-xs text-slate-500 mb-1 font-medium uppercase tracking-wider font-mono">Άμεση Τεχνική Υποστήριξη</div>
                <Link to="tel:+302100000000" className="flex items-center gap-3 text-lg font-bold text-indigo-400 hover:text-indigo-300 transition-colors group">
                  <Phone className="h-5 w-5 animate-pulse" />
                  <span>+30 215 2158367</span>
                </Link>
              </li>
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-bold tracking-tight text-foreground uppercase font-sans">Κοινωνικα Δικτυα</h3>
            <div className="flex gap-2">
              {[{
              icon: Facebook,
              label: "Facebook",
              href: "#"
            }, {
              icon: Twitter,
              label: "Twitter",
              href: "#"
            }, {
              icon: Linkedin,
              label: "LinkedIn",
              href: "#"
            }, {
              icon: Instagram,
              label: "Instagram",
              href: "#"
            }].map((social, i) => <Button key={i} data-index={i} variant="outline" size="icon" className="rounded-full border-white/10 bg-slate-900/50 hover:bg-cyan-500 hover:text-white hover:border-cyan-500 transition-all duration-300" asChild>
                  <Link to={social.href} aria-label={social.label} newTab>
                    <social.icon className="h-4 w-4" />
                  </Link>
                </Button>)}
            </div>

            <div className="pt-4">
              <div className="bg-slate-900/50 border border-white/10 rounded-xl p-4 backdrop-blur-xl shadow-2xl shadow-black/40">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-400 font-mono">System Status</span>
                  <Activity className="h-4 w-4 text-cyan-400" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-400"></span>
                  </span>
                  <span className="text-sm font-semibold text-foreground">All Systems Operational</span>
                </div>
                <div className="w-full bg-slate-800 h-1 mt-3 rounded-full overflow-hidden">
                  <div className="bg-cyan-400 h-full w-full animate-[pulse_3s_ease-in-out_infinite]" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-12 bg-white/10" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <p>© {currentYear} <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592747/img/ds_services_logo.png" alt="Ds-Services logo" className="h-5 inline-block align-baseline" />. Με επιφύλαξη παντός δικαιώματος.</p>
          <div className="flex gap-6">
            <Link to="#" className="hover:text-cyan-400 transition-colors">Πολιτική Απορρήτου</Link>
            <Link to="#" className="hover:text-cyan-400 transition-colors">Όροι Χρήσης</Link>
          </div>
        </div>
      </div>

      {}
      <Button variant="default" size="icon" className={`fixed bottom-8 right-8 z-50 rounded-full shadow-2xl shadow-cyan-500/20 bg-cyan-600 hover:bg-cyan-500 transition-all duration-500 ${showBackToTop ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"}`} onClick={scrollToTop} aria-label="Επιστροφή στην κορυφή">
        <ArrowUp className="h-5 w-5" />
      </Button>
    </section>;
}